import { graphql } from 'gatsby';
import { PageTemplateQuery, SanityPopIn } from 'graphql-types';
import React from 'react';
import { SanityHomeSection } from '../../graphql-types';
import { PopIn } from '../components/pop-in/PopIn';
import { HomeSection } from '../components/section/HomeSection';
import { Sections, SectionsType } from '../components/section/Sections';
import { SEO } from '../components/seo/Seo';
import { useKoreanAddressFix } from '../utils/useKoreanAddressFix';
import * as styles from './pageTemplate.module.scss';

interface Props {
  data: PageTemplateQuery;
}

const PageTemplate: React.FC<Props> = ({ data }) => {
  // Apply Korean address fix to all page data
  const fixedData = useKoreanAddressFix(data);
  
  return (
    <>
      {fixedData.sanityPage?.popIn && (
        <PopIn
          data={fixedData.sanityPage?.popIn as SanityPopIn}
          pageId={fixedData.sanityPage.id}
        />
      )}
      <SEO rawMeta={fixedData?.sanityPage?._rawMetadata} />
      {fixedData.sanityPage?.type === 'home' && (
        <div className={styles.homeSection}>
          <HomeSection section={fixedData?.sanityPage.homeHeader as SanityHomeSection} />
        </div>
      )}
      <Sections sections={fixedData?.sanityPage?.sectionsPageList as SectionsType} />
    </>
  );
};

export function Head() {
  return (
    <>
      <script
        type="application/ld+json"
        id="gtagManager"
        src="https://www.googletagmanager.com/gtag/js?id=G-CPD641WMLY"
      ></script>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());

      gtag('config', 'G-CPD641WMLY', {'send_page_view':false});
      gtag('config', 'GTM-KJSJGRLX', {'send_page_view':false});
`,
        }}
      ></script>
    </>
  );
}

export default PageTemplate;

export const PAGETEMPLATE = graphql`
  query PageTemplate($id: String) {
    sanityPage(id: { eq: $id }) {
      countrySettings {
        ...countrySettingsFragment
      }

      popIn {
        enabled
        _rawTitle(resolveReferences: { maxDepth: 10 })
        _rawDescription(resolveReferences: { maxDepth: 10 })
        _rawCtaLabel(resolveReferences: { maxDepth: 10 })
        _rawCtaLink(resolveReferences: { maxDepth: 10 })
        _rawImage(resolveReferences: { maxDepth: 10 })
        videoUrl
        image {
          asset {
            gatsbyImageData
          }
        }
      }
      _rawMetadata(resolveReferences: { maxDepth: 10 })
      id
      pageSlug {
        language
        text
      }
      type
      homeHeader {
        _rawHomeTextSection(resolveReferences: { maxDepth: 10 })
        homeLogoSection {
          logoAlt
          logoImg {
            asset {
              gatsbyImageData(placeholder: BLURRED)
            }
          }
        }
        _rawPatient(resolveReferences: { maxDepth: 10 })
        _rawPatientCta(resolveReferences: { maxDepth: 10 })
        _rawPatientSubtitle(resolveReferences: { maxDepth: 10 })
        _rawPatientTitle(resolveReferences: { maxDepth: 10 })
        _rawTherapist(resolveReferences: { maxDepth: 10 })
        _rawTherapistCta(resolveReferences: { maxDepth: 10 })
        _rawTherapistSubtitle(resolveReferences: { maxDepth: 10 })
        _rawTherapistTitle(resolveReferences: { maxDepth: 10 })
        therapistPage {
          pageSlug {
            language
            text
          }
        }
        patientPage {
          pageSlug {
            language
            text
          }
        }
        patientImage {
          alt
          img {
            asset {
              gatsbyImageData
            }
          }
        }
        therapistImage {
          alt
          img {
            asset {
              gatsbyImageData
            }
          }
        }
      }

      sectionsPageList {
        ...titleSectionFragment
        ...centerTextSectionFragment
        ...newsletterSectionFragment
        ...formSectionFragment
        ...valuesSectionFragment
        ...videoTitleTextSectionFragment
        ...technoCardSectionFragment
        ...mapSectionFragment
        ...expertiseCardSectionFragment
        ...productCardSectionFragment
        ...textButtonImgBgSectionFragment
        ...textPortraitSectionFragment
        ...imageSectionFragment
        ...adaptativeSectionFragment
        ...commentsSectionFragment
        ...partnerSectionFragment
        ...columnsSectionFragment
        ...exampleSectionFragment
        ...advantageProductSectionFragment
        ...buttonSectionFragment
        ...hubspotFormSectionFragment
      }
    }
  }
`;
